/**
 * Counting how people get on with Throttr, in totals that name no one. The app sends
 * short notes to /api/e (worker/events.ts), such as "someone new arrived", "finished
 * setup" or "made an account", each with where this browser first came from (TikTok, a
 * search, a typed address) and whether the app runs from the home screen; the server
 * adds the country and the kind of device, and the owner reads the totals at
 * stats.anessbelbati.com. No cookie, no id, no address: this browser keeps only the hour
 * it first came, from where, and which one-time notes it has sent, so each goes once.
 * Opening a page with ?count=off stops it on that browser (?count=on starts it again),
 * and the owner's account stops it on every device it is used on.
 */

export type CountEvent =
  | 'new' | 'open' | 'visit' | 'step' | 'setup' | 'demo' | 'set' | 'meal' | 'log' | 'workout'
  | 'account' | 'signin' | 'ai' | 'ai_kind' | 'ai_wall' | 'limit' | 'refused' | 'install' | 'share'

export const STORE = 'hyp.count'
const HOUR = 3_600_000
const DAY = 24 * HOUR
const BATCH = 20
/** The page's first notes go this soon: people from a video often leave within seconds. */
const FIRST_WAIT_MS = 2000
/**
 * Later notes wait this long for company, or for the page to be left, so going through
 * setup costs the server a few requests rather than one a screen.
 */
const WAIT_MS = 10_000
/** Back after this long away counts as opening the app again. */
const AWAY_MS = 30 * 60_000

interface Stored {
  /** The hour this browser first came. */
  first: number
  src: string
  ref: string
  /** The one-time notes already sent. */
  done: string[]
  off?: true
}

const PLATFORMS: readonly (readonly [RegExp, string])[] = [
  [/(^|\.)tiktok\.com$/, 'tiktok'],
  [/(^|\.)instagram\.com$/, 'instagram'],
  [/(^|\.)(youtube\.com|youtu\.be)$/, 'youtube'],
  [/(^|\.)(facebook\.com|fb\.com|fb\.me|messenger\.com)$/, 'facebook'],
  [/(^|\.)(x\.com|twitter\.com|t\.co)$/, 'x'],
  [/(^|\.)reddit\.com$/, 'reddit'],
  [/(^|\.)snapchat\.com$/, 'snapchat'],
  [/(^|\.)pinterest\.[a-z.]+$|^pin\.it$/, 'pinterest'],
  [/(^|\.)linkedin\.com$|^lnkd\.in$/, 'linkedin'],
  [/(^|\.)google\.[a-z.]+$/, 'google'],
  [/(^|\.)bing\.com$/, 'bing'],
  [/(^|\.)duckduckgo\.com$/, 'duckduckgo'],
  [/(^|\.)whatsapp\.com$|^wa\.me$/, 'whatsapp'],
  [/(^|\.)telegram\.(org|me)$|^t\.me$/, 'telegram'],
  [/(^|\.)linktr\.ee$/, 'linktree'],
]
const PLATFORM_NAMES = new Set(PLATFORMS.map(([, name]) => name))
const ALIASES: Readonly<Record<string, string>> = { tt: 'tiktok', ig: 'instagram', insta: 'instagram', yt: 'youtube', fb: 'facebook', twitter: 'x' }
/** The browsers inside other apps say whose they are; the same list as src/lib/install.ts. */
const IN_APP: readonly (readonly [RegExp, string])[] = [
  [/musical_ly|BytedanceWebview|TikTok/i, 'tiktok'],
  [/Instagram/i, 'instagram'],
  [/FBAN|FBAV|FB_IAB|FBIOS/i, 'facebook'],
  [/Snapchat/i, 'snapchat'],
  [/Twitter/i, 'x'],
  [/LinkedInApp/i, 'linkedin'],
  [/Pinterest/i, 'pinterest'],
]
const OWN = /(^|\.)throttr\.com$|^gym\.anessbelbati\.com$|^localhost$|^127\.0\.0\.1$/
/** The links for the bios (public/_redirects), each by where it is posted. */
const BIO_LINKS = new Map([['tiktok', 'tiktok'], ['insta', 'instagram']])

/** The bio link a page was opened at ('tiktok', 'insta'), or null. */
export function bioLink(path: string): string | null {
  const name = /^\/([a-z]+)\/?$/.exec(path)?.[1]
  return name !== undefined && BIO_LINKS.has(name) ? name : null
}

/** Lower case, letters, digits and . _ - only, at most 40 characters: what the server takes. */
export const clean = (s: string): string => s.toLowerCase().replace(/[^a-z0-9._-]+/g, '').slice(0, 40)

const platform = (name: string): string | null => {
  const n = ALIASES[name] ?? name
  return PLATFORM_NAMES.has(n) ? n : null
}

/**
 * Where a browser came from the first time: a link's own tag (?utm_source=, a bio link such
 * as /tiktok, or ?ref= naming a platform), else the site that sent it, else the app whose
 * browser it is, else "direct" (a typed address, or an app that hides where the tap came
 * from). `ref` keeps ?ref= (or ?utm_campaign=, or the bio link's name) as it is, so a link in
 * a bio or a post can be told apart.
 */
export function sourceOf(referrer: string, search: string, ua: string, path = ''): { src: string; ref: string } {
  const params = new URLSearchParams(search)
  const link = bioLink(path)
  const ref = clean(params.get('ref') ?? params.get('utm_campaign') ?? '') || (link ?? '')
  const utm = clean(params.get('utm_source') ?? '')
  if (utm) return { src: platform(utm) ?? utm, ref }
  if (link) return { src: BIO_LINKS.get(link)!, ref }
  let host = ''
  try {
    host = referrer ? new URL(referrer).hostname.toLowerCase().replace(/^www\./, '') : ''
  } catch { /* not an address */ }
  if (host && !OWN.test(host)) return { src: PLATFORMS.find(([re]) => re.test(host))?.[1] ?? clean(host), ref }
  const tagged = ref ? platform(ref) : null
  if (tagged) return { src: tagged, ref }
  return { src: IN_APP.find(([re]) => re.test(ua))?.[1] ?? 'direct', ref }
}

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(STORE)
    if (!raw) return null
    const s = JSON.parse(raw) as Partial<Stored>
    if (typeof s.first !== 'number' || !Array.isArray(s.done)) return null
    return {
      first: s.first, src: typeof s.src === 'string' ? s.src : 'direct', ref: typeof s.ref === 'string' ? s.ref : '',
      done: s.done.filter((d): d is string => typeof d === 'string'), ...(s.off ? { off: true as const } : {}),
    }
  } catch {
    return null
  }
}

function write(s: Stored): void {
  try {
    localStorage.setItem(STORE, JSON.stringify(s))
  } catch { /* storage full or blocked: the note may go again */ }
}

const standalone = (): boolean => {
  try {
    return matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true
  } catch {
    return false
  }
}

let state: Stored | null = null
let queue: [string, string][] = []
let timer: ReturnType<typeof setTimeout> | undefined
let started = false
let sent = false

function flush(): void {
  clearTimeout(timer)
  timer = undefined
  while (state && queue.length) {
    sent = true
    const body = JSON.stringify({
      e: queue.splice(0, BATCH), s: state.src, r: state.ref, m: standalone() ? 'app' : 'browser',
      f: state.first, a: Math.max(0, Math.floor((Date.now() - state.first) / DAY)),
    })
    try {
      // keepalive lets it finish after the page closes; no cookie goes with it.
      void fetch('/api/e', { method: 'POST', body, keepalive: true, credentials: 'omit', headers: { 'content-type': 'text/plain' } }).catch(() => {})
    } catch {
      try { navigator.sendBeacon('/api/e', body) } catch { /* nothing more to try */ }
    }
  }
}

/** A note each time it happens. */
export function count(event: CountEvent, detail = ''): void {
  try {
    if (!state || state.off) return
    queue.push([event, clean(detail)])
    if (queue.length >= BATCH) flush()
    else if (timer === undefined) timer = setTimeout(flush, sent ? WAIT_MS : FIRST_WAIT_MS)
  } catch { /* counting never gets in the way */ }
}

/** A note this browser sends once, ever: the first set, the account made. `key` says what counts as the same note. */
export function countOnce(event: CountEvent, detail = '', key = detail ? `${event}:${clean(detail)}` : event): void {
  try {
    if (!state) return
    // Read again: another tab may have sent it since.
    const s = read()
    if (!s || s.off || s.done.includes(key)) return
    s.done.push(key)
    write(s)
    state = s
    count(event, detail)
  } catch { /* counting never gets in the way */ }
}

/** Nothing more from this browser: the owner's devices, and anyone who opened ?count=off. */
export function stopCounting(): void {
  try {
    queue = []
    clearTimeout(timer)
    timer = undefined
    state = null
    const s = read() ?? { first: 0, src: '', ref: '', done: [] }
    if (!s.off) write({ ...s, off: true })
  } catch { /* nothing to stop */ }
}

/**
 * At the page's start. `where` is the app, or a page of the website around it. The first
 * visit notes where this browser came from; the app notes each time it is opened, and
 * again after half an hour away.
 */
export function startCounting(where: 'app' | 'site'): void {
  if (started) return
  started = true
  try {
    const flag = new URLSearchParams(location.search).get('count')
    let s = read()
    if (flag === 'off') return stopCounting()
    if (flag === 'on' && s?.off) {
      delete s.off
      write(s)
    }
    if (s?.off) return
    const arrived = s === null || s.first === 0
    if (arrived) {
      s = { first: Math.floor(Date.now() / HOUR) * HOUR, ...sourceOf(document.referrer, location.search, navigator.userAgent, location.pathname), done: [] }
      write(s)
    }
    state = s
    if (arrived) countOnce('new', where, 'new')
    let awayAt = 0
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        awayAt = Date.now()
        flush()
      } else if (where === 'app' && awayAt && Date.now() - awayAt >= AWAY_MS) {
        awayAt = 0
        count('open')
      }
    })
    addEventListener('pagehide', flush)
    if (where === 'app') {
      count('open')
      if (standalone()) countOnce('install', 'home', 'install')
    } else {
      count('visit', location.pathname.replace(/^\/+|\/+$/g, '') || 'home')
    }
  } catch { /* counting never gets in the way */ }
}
